import Avatar from '@hellodarwin/core/lib/components/common/avatar';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import type { ProgramAdmin } from '@hellodarwin/core/lib/features/entities';
import { getFullName } from '@hellodarwin/core/lib/features/helpers';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import Email from '@hellodarwin/icons/dist/icons/Email';
import Phone from '@hellodarwin/icons/dist/icons/Phone';
import User from '@hellodarwin/icons/dist/icons/User';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import {
  TeamSummary,
  TeamSummaryActions,
  TeamSummaryContactLink,
  TeamSummaryIdentity,
  TeamSummaryList,
  TeamSummaryMember,
} from './styles';

interface ApplicationTeamSummaryProps {
  programAdmins: ProgramAdmin[];
}

interface ApplicationTeamMember {
  admin: ProgramAdmin;
  roles: string[];
}

const roleOrder = ['AccountManager', 'Analyst', 'Expert', 'NoRole'];

const ApplicationTeamSummary = ({
  programAdmins,
}: ApplicationTeamSummaryProps) => {
  const { t } = useTranslations();
  const theme = useTheme();
  const roleLabels: Record<string, string> = {
    AccountManager: t('program-workspace|applicationTeamRoleAccountManager'),
    Analyst: t('program-workspace|applicationTeamRoleAnalyst'),
    Expert: t('program-workspace|applicationTeamRoleExpert'),
    NoRole: t('program-workspace|applicationTeamRoleOther'),
  };

  const teamMembers = useMemo(() => {
    const membersById = new Map<string, ApplicationTeamMember>();

    for (const admin of programAdmins) {
      if (!admin.admin_id) continue;
      const role = admin.role || 'NoRole';
      const existing = membersById.get(admin.admin_id);
      if (existing) {
        if (!existing.roles.includes(role)) existing.roles.push(role);
        continue;
      }
      membersById.set(admin.admin_id, { admin, roles: [role] });
    }

    return Array.from(membersById.values()).map((member) => ({
      ...member,
      roles: member.roles.sort(
        (left, right) => roleOrder.indexOf(left) - roleOrder.indexOf(right),
      ),
    }));
  }, [programAdmins]);

  if (teamMembers.length === 0) return null;

  return (
    <TeamSummary>
      <Typography elementTheme="body3" bold>
        {t('program-workspace|applicationTeamTitle')}
      </Typography>
      <TeamSummaryList>
        {teamMembers.map(({ admin, roles }) => {
          const name = getFullName(admin.first_name, admin.last_name);
          return (
            <TeamSummaryMember key={admin.admin_id}>
              <Avatar
                src={admin.picture}
                alt={name}
                size={theme.spacing.xl}
                shape="round"
                background={theme.colors.purple_4}
                style={{ color: theme.colors.purple_2 }}
              >
                <User size={theme.spacing.md} />
              </Avatar>
              <TeamSummaryIdentity>
                <Typography elementTheme="body2" bold>
                  {name}
                </Typography>
                <Typography elementTheme="body3" color={theme.colors.grey_2}>
                  {roles
                    .map((role) => roleLabels[role] || roleLabels.NoRole)
                    .join(' · ')}
                </Typography>
              </TeamSummaryIdentity>
              {admin.phone || admin.email ? (
                <TeamSummaryActions>
                  {admin.phone ? (
                    <TeamSummaryContactLink
                      href={`tel:${admin.phone}`}
                      aria-label={t('program-workspace|applicationTeamCall', {
                        name,
                      })}
                      title={admin.phone}
                    >
                      <Phone size={theme.spacing.sm + theme.spacing.xs} />
                    </TeamSummaryContactLink>
                  ) : null}
                  {admin.email ? (
                    <TeamSummaryContactLink
                      href={`mailto:${admin.email}`}
                      aria-label={t('program-workspace|applicationTeamEmail', {
                        name,
                      })}
                      title={admin.email}
                    >
                      <Email size={theme.spacing.sm + theme.spacing.xs} />
                    </TeamSummaryContactLink>
                  ) : null}
                </TeamSummaryActions>
              ) : null}
            </TeamSummaryMember>
          );
        })}
      </TeamSummaryList>
    </TeamSummary>
  );
};

export default ApplicationTeamSummary;
